import { GuestField } from '@common/components/BookingForms';
import { useTranslations } from '@lang';
import { FC, useEffect } from 'react';
import { Field, FieldArray } from 'redux-form';
import { useRouter } from 'shared-routing';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuests } from '../../../../components/AddExistingGuests';
import { SectionWrapper } from '../../../../components/BookingForms/Section';
import { Button } from '../../../../components/Button';
import { TextareaField } from '../../../../components/FormElements';
import Portal from '../../../../components/Portal';
import { StickyBarContent } from '../../../../components/StickyBar';
import { Routes } from '../../../../constants';
import { NotesWrapper, StyledDateRangeIndicator } from './GuestDetailsStep.styles';
import { GuestDetailsStepProps, GuestDetailsStepReduxProps } from './GuestDetailsStep.types';

const routesMap = {
  room: Routes.ROOM_DETAIL,
  rate: Routes.RATE_DETAIL,
};

export const GuestDetailsStep = ({
  basketGuests,
  loggedGuest,
  handleSubmit,
  stickyBarId,
  selectedEntity,
  change,
  selectedGuests = [],
  valid,
  submitting,
  datePartyData,
  isSignInModalOpen = false,
  handleSignInModalOpen,
  goToNextStep,
  addToBasket,
  metaEntity,
  availableExtras,
}: GuestDetailsStepProps & GuestDetailsStepReduxProps) => {
  const { t } = useTranslations();
  const router = useRouter();
  useEffect(() => {
    if (selectedEntity) {
      return;
    }

    router.push({
      pathname: routesMap[metaEntity.type],
      query: {
        id: metaEntity.id,
      },
    });
  }, [selectedEntity, router, metaEntity.type, metaEntity.id]);

  const hasExtras = availableExtras?.length;
  const { adults, children } = datePartyData;

  const onSubmit = ({ guests, note }) => {
    if (hasExtras) {
      goToNextStep();
    } else {
      addToBasket({ guestDetails: guests, note }).then(() => goToNextStep());
    }
  };

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        <FieldArray
          name="guests"
          component={AddExistingGuests}
          selectedGuests={selectedGuests}
          existingGuests={basketGuests as Guest[]}
          minimumGuests={adults}
          maximumGuests={adults}
          isGenderSpecific={false}
          guest={loggedGuest}
        />
        <FieldArray
          name="guests"
          component={GuestField as FC}
          props={{
            fieldChange: change,
            minimumGuests: adults,
            maximumGuests: adults,
            withGenderSelect: false,
            withLeadGuest: true,
            handleSignInModalOpen,
            isSignInModalOpen,
          }}
        />
        {children ? <>+{t('basket.children', { children })}</> : null}
        <NotesWrapper>
          <Field
            isFullWidth
            component={TextareaField}
            labelWidth="150px"
            name="note"
            label={t('rooms.notes')}
            id="additional-notes"
          />
        </NotesWrapper>
      </SectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={datePartyData.fromDate}
              toDate={datePartyData.toDate}
            />
          }
          displayedPrice={selectedEntity?.pricePerStay || null}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!valid || submitting}
            >
              {t(hasExtras ? 'spa.package.nextStep' : 'product.ctaButton')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
